import React from 'react';

interface Props {
  /** Multi-line identity to repeat across the page. Each entry becomes one row. */
  lines: string[];
  /** Width / height in pixels of the host (PDF page). */
  width: number;
  height: number;
  /** 0–1 opacity for the watermark text. */
  opacity?: number;
  /** Font size in px. */
  fontSize?: number;
}

/**
 * Diagonal repeating-text watermark drawn over a SecureViewer page.
 *
 * Implementation: an SVG <pattern> tiled across the surface. The text rotates
 * inside the pattern (not the SVG) so the tile boundaries stay aligned and the
 * watermark looks consistent at any zoom. `pointer-events: none` keeps the
 * overlay from intercepting clicks; `user-select: none` prevents the text
 * from being highlighted/copied.
 */
const WatermarkOverlay: React.FC<Props> = ({
  lines,
  width,
  height,
  opacity = 0.18,
  fontSize = 13,
}) => {
  // Pattern tile sized so the diagonal text repeats roughly every 220px.
  const tile = 240;
  const patternId = React.useId();

  return (
    <svg
      role="presentation"
      aria-hidden="true"
      width={width}
      height={height}
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        userSelect: 'none',
      }}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <pattern
          id={patternId}
          width={tile}
          height={tile}
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(-28)"
        >
          {lines.map((line, idx) => (
            <text
              key={`${idx}-${line}`}
              x={0}
              y={fontSize + idx * (fontSize + 4)}
              fontFamily="system-ui, -apple-system, Segoe UI, Roboto, sans-serif"
              fontSize={fontSize}
              fontWeight={600}
              fill="currentColor"
              opacity={opacity}
            >
              {line}
            </text>
          ))}
        </pattern>
      </defs>
      <rect width={width} height={height} fill={`url(#${patternId})`} />
    </svg>
  );
};

export default WatermarkOverlay;
